import { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Download, Loader2 } from 'lucide-react';
import { founderApi } from '@/lib/founderApi';
import { usePageMeta } from '@/lib/usePageMeta';
import { FounderFrame } from '@/components/founder/FounderFrame';

const KIND = { fact: 'Your answer', assumption: 'Assumption', estimate: 'Estimate' };
const fmt = (v, k) => (k === 'money' ? `${v < 0 ? '−' : ''}$${Math.abs(v).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : k === 'pct' ? `${v}%` : v.toLocaleString());

export default function BlueprintDocument() {
  const { bid } = useParams();
  usePageMeta({ title: 'Founder Business Blueprint', path: `/founder/blueprint/${bid}`, robots: 'noindex' });
  const navigate = useNavigate();
  const [doc, setDoc] = useState(null);
  useEffect(() => {
    founderApi.document(bid).then(setDoc).catch((e) => navigate(e?.response?.status === 401 ? '/founder/login' : '/founder/workspace', { replace: true }));
  }, [bid, navigate]);
  if (!doc) return <FounderFrame testid="blueprint-doc-loading"><Loader2 className="animate-spin text-gold" /></FounderFrame>;
  return (
    <FounderFrame eyebrow="ORIGENNE™ · Founder Business Blueprint™" testid="blueprint-document-page">
      <Link to="/founder/workspace" className="text-[11px] uppercase tracking-[0.2em] text-matte/60 inline-flex items-center gap-2" data-testid="blueprint-doc-back"><ArrowLeft size={12} /> Founder Workspace</Link>
      <h1 className="mt-6 font-serif-display text-4xl sm:text-5xl lg:text-6xl tracking-tighter text-matte" data-testid="blueprint-doc-name">{doc.business_name}</h1>
      <p className="mt-3 text-sm text-matte/60">{doc.stages_complete} of 6 stages complete · Built only from your saved answers. Estimates are educational, not guarantees.</p>
      <a href={founderApi.pdfUrl(bid)} className="btn-primary mt-8" data-testid="blueprint-doc-pdf-button"><Download size={14} /> Download PDF</a>
      {doc.sections.map((s) => (
        <section key={s.title} className="mt-14" data-testid={`blueprint-doc-section-${s.stage || 'insights'}`}>
          <div className="flex flex-wrap items-baseline justify-between gap-2">
            <h2 className="font-serif-display text-3xl text-matte">{s.title}</h2>
            {s.status && s.status !== 'complete' && <span className="text-[10px] uppercase tracking-[0.2em] text-amber-700">Incomplete</span>}
          </div>
          {!s.items.length && !s.estimates.length && !s.tasks && <p className="mt-3 text-sm text-matte/50">No answers saved yet.</p>}
          <dl className="mt-4 divide-y divide-matte/10">
            {s.items.map((i) => (
              <div key={i.label} className="py-3">
                <dt className="text-[11px] uppercase tracking-[0.14em] text-matte/50">{i.label} · <span className={i.kind === 'assumption' ? 'text-amber-700' : 'text-gold'}>{KIND[i.kind]}</span></dt>
                <dd className="mt-1 text-sm text-matte whitespace-pre-line break-words">{i.value}</dd>
              </div>
            ))}
          </dl>
          {s.estimates.length > 0 && (
            <div className="mt-4 rounded-xl bg-white border border-matte/10 p-4" data-testid={`blueprint-doc-estimates-${s.stage}`}>
              <div className="text-[10px] uppercase tracking-[0.22em] text-gold mb-2">Calculated estimates</div>
              {s.estimates.map(([l, v, k]) => <div key={l} className="flex justify-between gap-4 py-1 text-sm"><span>{l}</span><span className="font-medium">{fmt(v, k)}</span></div>)}
            </div>
          )}
          {s.tasks && Object.entries(doc.weeks).map(([w, label]) => (
            <div key={w} className="mt-4">
              <div className="text-[11px] uppercase tracking-[0.18em] text-matte/60">{label}</div>
              <ul className="mt-1 text-sm list-disc pl-5">
                {(s.tasks[w] || []).map((t) => <li key={t.id} className={t.done ? 'line-through text-matte/50' : ''}>{t.title}{t.due ? ` · ${t.due}` : ''}</li>)}
                {!(s.tasks[w] || []).length && <li className="list-none -ml-5 text-matte/40">No tasks yet.</li>}
              </ul>
            </div>
          ))}
        </section>
      ))}
    </FounderFrame>
  );
}
